import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, Renderer2, signal, TemplateRef, ViewChild, ViewContainerRef, WritableSignal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-copilot-search-input',
    templateUrl: './copilot-search-input.component.html',
    styleUrls: ['./copilot-search-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CopilotSearchInputComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CopilotSearchInputComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
    @Input() isLoading: boolean = false;
    @Input() exampleSearches: string[] = [];
    @Output() searchTriggered = new EventEmitter<void>();
    // `TemplateRef<T>` is the template context type (values exposed via `let-...`).
    // We attach this template without a context, so `unknown` is the safest/most accurate type.
    @ViewChild('overlayTemplate') overlayTemplate!: TemplateRef<unknown>;
    @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;

    animatedText: string = '';
    isShowLogo: boolean = true;
    readonly isQueryUnsearched: WritableSignal<boolean> = signal(false);

    private overlayRef: OverlayRef | null = null;
    private readonly textLimit: number = 40;
    private typingIntervalId: ReturnType<typeof setInterval> | null = null;
    private moveToNextExampleTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private charIndex: number = 0;
    private currentExampleIndex: number = 0;
    private readonly typingSpeed: number = 50;
    private readonly pauseBetweenExamples: number = 1000;
    private isTyping: boolean = false;
    private innerSearchText: string = '';

    onChange: (searchText: string) => void = () => { };
    onTouched: () => void = () => { };

    @Input()
    set searchText(value: string) {
        this.innerSearchText = value;
        this.onChange(this.innerSearchText);
    }

    get searchText(): string {
        return this.innerSearchText;
    }

    writeValue(value: string): void {
        if (value !== undefined) {
            this.innerSearchText = value;
            this.isQueryUnsearched.set(false);
        }
    }

    registerOnChange(fn: (searchText: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly overlay: Overlay, 
        private readonly viewContainerRef: ViewContainerRef,
        private readonly renderer: Renderer2
    ) { }

    ngOnInit(): void {
        this.startTypingAnimation();
    }

    onFocus(): void {
        this.isShowLogo = false;
        this.startTypingAnimation();
        if (this.searchText?.length > this.textLimit && !this.overlayRef?.hasAttached()) {
            this.openOverlay();
        }
    }

    onBlur(): void {
        this.isShowLogo = true;
    }
    
    startTypingAnimation(): void {
        this.resetTyping();

        if (!Array.isArray(this.exampleSearches) || this.exampleSearches.length === 0) {
            this.isTyping = false;
            return;
        }

        this.isTyping = true;
        this.currentExampleIndex = this.currentExampleIndex % this.exampleSearches.length;

        const currentText = this.exampleSearches[this.currentExampleIndex] ?? '';
        this.typingIntervalId = setInterval(() => {
            if (this.charIndex < currentText.length) {
                this.animatedText += currentText[this.charIndex];
                this.charIndex++;
                this.cdr.detectChanges();
                return;
            }

            this.stopTypingAnimation();
            this.moveToNextExampleTimeoutId = setTimeout(() => this.moveToNextExample(), this.pauseBetweenExamples);
        }, this.typingSpeed);
    }

    moveToNextExample(): void {
        if (this.isTyping) return;
        if (!Array.isArray(this.exampleSearches) || this.exampleSearches.length === 0) return;

        this.charIndex = 0;
        this.animatedText = '';
        this.currentExampleIndex = (this.currentExampleIndex + 1) % this.exampleSearches.length;

        this.startTypingAnimation();
    }

    stopTypingAnimation(): void {
        if (this.typingIntervalId != null) {
            clearInterval(this.typingIntervalId);
            this.typingIntervalId = null;
        }
        this.isTyping = false;
    }

    resetTyping(): void {
        this.stopTypingAnimation();
        if (this.moveToNextExampleTimeoutId != null) {
            clearTimeout(this.moveToNextExampleTimeoutId);
            this.moveToNextExampleTimeoutId = null;
        }
        this.charIndex = 0;
        this.animatedText = '';
    }

    handleUserTyping(): void {
        this.isQueryUnsearched.set(true);
        if (this.searchText && this.isTyping) {
            this.stopTypingAnimation();
        } else if (!this.searchText && !this.isTyping) {
            this.startTypingAnimation();
        }
        this.checkTextLength();
    }

    handleOverlayTyping(): void {
        this.isQueryUnsearched.set(true);
        this.checkTextLength();
    }

    restrictQuotes(event: KeyboardEvent): void {
        if (event.key === '"' || event.key === "'") {
            event.preventDefault();
        }
    }

    sanitizePaste(event: ClipboardEvent): void {
        event.preventDefault();
        const pastedText = event.clipboardData?.getData('text') || '';
        const sanitizedText = pastedText.replace(/['"]/g, '');

        const inputElement = event.target as HTMLInputElement;
        const selectionStart = inputElement.selectionStart ?? 0;
        const selectionEnd = inputElement.selectionEnd ?? selectionStart;

        this.searchText =
            (this.searchText?.slice(0, selectionStart) || '') +
            sanitizedText +
            (this.searchText?.slice(selectionEnd) || '');
        this.isQueryUnsearched.set(true);

        setTimeout(() => {
            const newCaretPos = selectionStart + sanitizedText.length;
            inputElement.setSelectionRange(newCaretPos, newCaretPos);
        });

        this.checkTextLength();
    }

    triggerSearch(): void {
        this.isQueryUnsearched.set(false);
        this.closeOverlay();
        this.searchTriggered.emit();
    }

    onEnterPress(event?: KeyboardEvent): void {
        event?.preventDefault();
        if (this.searchText.trim()) {
            this.triggerSearch();
        }
    }

    ngAfterViewInit(): void {
        this.overlayRef = this.overlay.create({
            positionStrategy: this.overlay
                .position()
                .flexibleConnectedTo(this.inputElement.nativeElement)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetY: -30,
                        offsetX: -15,
                    },
                ]),
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
        });

        this.overlayRef.backdropClick().subscribe(() => this.closeOverlay());
    }

    checkTextLength(extrawidth?: number): void {
        if ((this.searchText?.length ?? 0) > this.textLimit && !this.overlayRef?.hasAttached()) {
            this.openOverlay(extrawidth);
            return;
        }

        if ((this.searchText?.length ?? 0) <= this.textLimit) {
            const textarea = this.overlayRef?.overlayElement?.querySelector('textarea') as HTMLTextAreaElement | null;
            const cursorPositionInTextArea = (textarea && typeof textarea.selectionStart === 'number')
                ? textarea.selectionStart
                : null;

            if (this.overlayRef?.hasAttached()) {
                this.closeOverlay();
            }

            setTimeout(() => {
                const inputEl = this.inputElement?.nativeElement as HTMLInputElement | null;
                if (!inputEl) return;

                inputEl.focus();

                const fallbackPos = Math.max(0, (this.searchText?.length ?? 0));
                const inputSelectionStart = (typeof inputEl.selectionStart === 'number') ? inputEl.selectionStart : null;
                const desiredPos = (cursorPositionInTextArea != null)
                    ? cursorPositionInTextArea
                    : (inputSelectionStart != null ? inputSelectionStart : fallbackPos);

                const clamp = (n: number, min = 0, max = (this.searchText?.length ?? 0)) => Math.max(min, Math.min(max, n));
                const finalPos = clamp(desiredPos);

                if (typeof inputEl.setSelectionRange === 'function') {
                    try {
                        inputEl.setSelectionRange(finalPos, finalPos);
                    } catch { }
                }
            }, 0);
        }
    }

    handleFocusOut(): void {
        if (this.searchText.length <= this.textLimit) {
            this.closeOverlay();
        }
    }

    ngOnDestroy(): void {
        this.resetTyping();
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
    }

    clearText(): void {
        this.searchText = '';
        this.isQueryUnsearched.set(false);
        this.handleFocusOut();
    }

    private openOverlay(extrawidth: number = 0): void {
        if (this.overlayRef && !this.overlayRef.hasAttached() && this.overlayTemplate) {
            this.overlayRef.attach(new TemplatePortal(this.overlayTemplate, this.viewContainerRef));
            const inputWidth = this.inputElement.nativeElement.offsetWidth + 95 - extrawidth;
            const overlayElement = this.overlayRef.overlayElement;
            this.renderer.setStyle(overlayElement, 'width', `${inputWidth}px`);

            const cursorPosition = this.inputElement.nativeElement.selectionStart || this.searchText.length;

            setTimeout(() => {
                const textarea = this.overlayRef?.overlayElement.querySelector('textarea');
                if (textarea) {
                    textarea.focus();
                    textarea.setSelectionRange(cursorPosition, cursorPosition);
                }
            }, 0);
        }
    }

    private closeOverlay(): void {
        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
        }
    }
}
